import { useState } from "react";
import api, { fmtErr, toastErr } from "../../api";
import { toast } from "sonner";

export function QuoteActions({ q, onChanged }) {
  const [emailOpen, setEmailOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sendSms, setSendSms] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!q) return null;
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/quotes/${q.id}/pdf`;

  const fetchPdf = async () => {
    const r = await fetch(pdfUrl, { credentials: "include" });
    if (!r.ok) throw new Error(`PDF ${r.status}`);
    return r.blob();
  };
  const download = async () => {
    try {
      const blob = await fetchPdf();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${q.quote_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't download the PDF"); }
  };
  const share = async () => {
    setBusy(true);
    try {
      const blob = await fetchPdf();
      const file = new File([blob], `${q.quote_number}.pdf`, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Quote ${q.quote_number}` });
      } else {
        await download();
        toast("File sharing isn't supported here — downloaded the PDF instead");
      }
    } catch (e) { if (e?.name !== "AbortError") toast.error("Couldn't share the PDF"); }
    setBusy(false);
  };
  const sendEmail = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/quotes/${q.id}/send`, { message, send_sms: sendSms });
      if (r.data.email_sent === false) toast.error("Quote updated, but the email could not be sent — use Share or Download instead");
      else toast.success(`Emailed to ${r.data.quote_sent_to} with the PDF attached`);
      setEmailOpen(false); setMessage("");
      onChanged?.(r.data);
    } catch (e) { toastErr(e); }
    setBusy(false);
  };
  const markSent = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/quotes/${q.id}/mark-sent`);
      toast.success("Marked as sent");
      onChanged?.(r.data);
    } catch (e) { toastErr(e); }
    setBusy(false);
  };
  const copyLink = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/quotes/${q.id}/link`);
      await navigator.clipboard.writeText(r.data.url);
      toast.success("Accept link copied — text or email it yourself");
    } catch (e) { toastErr(e); }
    setBusy(false);
  };

  const btn = "h-10 rounded-md border px-3 text-xs font-bold";
  return (
    <div data-testid={`quote-actions-${q.id}`}>
      <div className="flex flex-wrap gap-2">
        {q.email && (
          <button data-testid={`quote-email-btn-${q.id}`} onClick={() => setEmailOpen(!emailOpen)}
            className={`${btn} border-primary text-primary`}>Email quote</button>
        )}
        {q.acceptance_method !== "paper" && (
          <button data-testid={`quote-copy-link-${q.id}`} onClick={copyLink} disabled={busy}
            className={`${btn} border-border`}>Copy link</button>
        )}
        <button data-testid={`quote-share-btn-${q.id}`} onClick={share} disabled={busy}
          className={`${btn} border-primary text-primary`}>Share</button>
        <button data-testid={`quote-download-btn-${q.id}`} onClick={download} disabled={busy}
          className={`${btn} border-border`}>Download PDF</button>
        {q.status === "draft" && (
          <button data-testid={`quote-mark-sent-${q.id}`} onClick={markSent} disabled={busy}
            className={`${btn} border-border`}>Mark as sent</button>
        )}
      </div>
      {emailOpen && (
        <div className="mt-2 space-y-2 rounded-md border-2 border-primary p-3" data-testid={`quote-email-panel-${q.id}`}>
          <p className="text-xs text-muted-foreground">To {q.email} — the PDF is attached. Add a short note (optional):</p>
          <textarea data-testid={`quote-email-message-${q.id}`} className="field-input w-full" rows={2} value={message}
            placeholder="e.g. Thanks for having me by — any questions, just reply." onChange={(e) => setMessage(e.target.value)} />
          {q.phone && q.acceptance_method !== "paper" && (
            <label className="flex items-center gap-2 text-xs font-semibold">
              <input type="checkbox" data-testid={`quote-email-sms-${q.id}`} checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} />
              Also text the accept link to {q.phone}
            </label>
          )}
          <button data-testid={`quote-email-send-${q.id}`} onClick={sendEmail} disabled={busy}
            className="h-11 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground disabled:opacity-50">
            {busy ? "Sending…" : "Send email with PDF"}
          </button>
        </div>
      )}
    </div>
  );
}
